import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AchievementRewards = ({ user, userStats }) => {
  const [unlockedFeatures, setUnlockedFeatures] = useState([]);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && userStats) {
      calculateUnlockedFeatures();
    }
  }, [user, userStats]);

  const calculateUnlockedFeatures = () => {
    setLoading(true);
    
    const features = [];
    const rewards = [];
    const totalPoints = userStats.total_points || 0;
    const totalAchievements = userStats.total_achievements || 0;
    const achievementsByCategory = userStats.achievements_by_category || {};

    // Point-based rewards
    const pointRewards = [
      {
        id: 'bronze_tier',
        name: 'Bronze Tier',
        description: 'Unlock bronze user badge and special profile color',
        requirement: 50,
        type: 'points',
        icon: '🥉',
        color: 'from-amber-600 to-amber-700',
        unlocked: totalPoints >= 50,
        features: ['Bronze Badge', 'Amber Profile Border', 'Priority Support']
      },
      {
        id: 'silver_tier',
        name: 'Silver Tier',
        description: 'Unlock silver badge and advanced analytics',
        requirement: 200,
        type: 'points',
        icon: '🥈',
        color: 'from-gray-400 to-gray-600',
        unlocked: totalPoints >= 200,
        features: ['Silver Badge', 'Advanced Analytics', 'Campaign Templates', 'Export Features']
      },
      {
        id: 'gold_tier',
        name: 'Gold Tier',
        description: 'Unlock gold badge and premium features',
        requirement: 500,
        type: 'points',
        icon: '🥇',
        color: 'from-yellow-400 to-yellow-600',
        unlocked: totalPoints >= 500,
        features: ['Gold Badge', 'Premium Templates', 'AI Voice Generation', 'White-label Options']
      },
      {
        id: 'platinum_tier',
        name: 'Platinum Tier',
        description: 'Unlock platinum status and exclusive features',
        requirement: 1000,
        type: 'points',
        icon: '💎',
        color: 'from-indigo-400 to-indigo-600',
        unlocked: totalPoints >= 1000,
        features: ['Platinum Badge', 'Custom AI Models', 'API Access', 'Dedicated Account Manager']
      }
    ];

    // Achievement-based rewards
    const achievementRewards = [
      {
        id: 'creative_master',
        name: 'Creative Master',
        description: 'Unlock advanced creative tools',
        requirement: 10,
        type: 'creative_achievements',
        icon: '🎨',
        color: 'from-blue-400 to-purple-600',
        unlocked: (achievementsByCategory.creative || 0) >= 10,
        features: ['Advanced Image Editor', 'Custom Filters', 'Brand Kit Manager']
      },
      {
        id: 'social_influencer',
        name: 'Social Influencer',
        description: 'Unlock social media automation',
        requirement: 5,
        type: 'social_achievements',
        icon: '📱',
        color: 'from-pink-400 to-red-600',
        unlocked: (achievementsByCategory.social || 0) >= 5,
        features: ['Auto-Posting', 'Social Analytics', 'Influencer Network']
      },
      {
        id: 'engagement_expert',
        name: 'Engagement Expert',
        description: 'Unlock engagement optimization tools',
        requirement: 8,
        type: 'engagement_achievements',
        icon: '💪',
        color: 'from-green-400 to-green-600',
        unlocked: (achievementsByCategory.engagement || 0) >= 8,
        features: ['A/B Testing', 'Engagement Analytics', 'Optimization Suggestions']
      }
    ];

    // Total achievement rewards
    const milestoneRewards = [
      {
        id: 'achievement_collector',
        name: 'Achievement Collector',
        description: 'Unlock collector badge and special features',
        requirement: 10,
        type: 'total_achievements',
        icon: '🏆',
        color: 'from-orange-400 to-red-600',
        unlocked: totalAchievements >= 10,
        features: ['Collector Badge', 'Achievement Showcase', 'Leaderboard Highlighting']
      },
      {
        id: 'completionist',
        name: 'Completionist',
        description: 'Unlock completionist status',
        requirement: 25,
        type: 'total_achievements',
        icon: '⭐',
        color: 'from-purple-400 to-pink-600',
        unlocked: totalAchievements >= 25,
        features: ['Completionist Badge', 'Early Access to Features', 'Beta Testing Access']
      }
    ];

    const allRewards = [...pointRewards, ...achievementRewards, ...milestoneRewards];
    
    const unlocked = allRewards.filter(reward => reward.unlocked);
    const available = allRewards.filter(reward => !reward.unlocked);

    setUnlockedFeatures(unlocked);
    setAvailableRewards(available);
    setLoading(false);
  };

  const getProgressPercentage = (reward) => {
    const totalPoints = userStats.total_points || 0;
    const totalAchievements = userStats.total_achievements || 0;
    const achievementsByCategory = userStats.achievements_by_category || {};

    switch (reward.type) {
      case 'points':
        return Math.min((totalPoints / reward.requirement) * 100, 100);
      case 'total_achievements':
        return Math.min((totalAchievements / reward.requirement) * 100, 100);
      case 'creative_achievements':
        return Math.min(((achievementsByCategory.creative || 0) / reward.requirement) * 100, 100);
      case 'social_achievements':
        return Math.min(((achievementsByCategory.social || 0) / reward.requirement) * 100, 100);
      case 'engagement_achievements':
        return Math.min(((achievementsByCategory.engagement || 0) / reward.requirement) * 100, 100);
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unlocked Features */}
      {unlockedFeatures.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <span className="text-2xl mr-2">🎁</span>
            Unlocked Rewards ({unlockedFeatures.length})
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {unlockedFeatures.map((reward) => (
              <div
                key={reward.id}
                className={`relative bg-gradient-to-r ${reward.color} rounded-lg p-4 text-white overflow-hidden`}
              >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-2 right-2 text-4xl">{reward.icon}</div>
                  <div className="absolute bottom-2 left-2 text-6xl opacity-20">✨</div>
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">{reward.icon}</span>
                    <h4 className="font-bold text-lg">{reward.name}</h4>
                  </div>
                  <p className="text-sm mb-3 opacity-90">{reward.description}</p>
                  
                  {/* Features List */}
                  <div className="space-y-1">
                    {reward.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-xs opacity-90">
                        <span className="mr-1">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Unlocked Badge */}
                  <div className="absolute top-2 right-2 bg-white bg-opacity-20 rounded-full px-2 py-1 text-xs font-bold">
                    UNLOCKED
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Rewards */}
      {availableRewards.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <span className="text-2xl mr-2">🎯</span>
            Available Rewards ({availableRewards.length})
          </h3>
          
          <div className="space-y-4">
            {availableRewards.map((reward) => {
              const progress = getProgressPercentage(reward);
              return (
                <div
                  key={reward.id}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3 opacity-50">{reward.icon}</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{reward.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{reward.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {reward.type === 'points' && `${userStats.total_points || 0}/${reward.requirement} points`}
                        {reward.type === 'total_achievements' && `${userStats.total_achievements || 0}/${reward.requirement} achievements`}
                        {reward.type === 'creative_achievements' && `${userStats.achievements_by_category?.creative || 0}/${reward.requirement} creative`}
                        {reward.type === 'social_achievements' && `${userStats.achievements_by_category?.social || 0}/${reward.requirement} social`}
                        {reward.type === 'engagement_achievements' && `${userStats.achievements_by_category?.engagement || 0}/${reward.requirement} engagement`}
                      </div>
                      <div className="text-xs text-gray-500">{Math.round(progress)}% complete</div>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                    <div 
                      className={`bg-gradient-to-r ${reward.color} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  
                  {/* Features Preview */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {reward.features.slice(0, 4).map((feature, index) => (
                      <div key={index} className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        🔒 {feature}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Rewards State */}
      {unlockedFeatures.length === 0 && availableRewards.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center">
          <div className="text-6xl mb-4">🎁</div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Rewards System Loading
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Start earning achievements to unlock exclusive rewards and features!
          </p>
        </div>
      )}
    </div>
  );
};

export default AchievementRewards;